import { 
  Truck, 
  LucideIcon,
  LayoutDashboard,
  Car,
  Users,
  Route,
  Wrench,
  Fuel,
  Coins,
  Package,
  AlertCircle,
  Settings,
  BarChart3,
  MapPin,
  Shield,
  ClipboardList,
  Map,
  CreditCard,
  FileText,
  Radio,
  AlertTriangle,
  Send,
  Bell,
  Receipt,
  ShoppingBag
} from 'lucide-react';

export interface ModuleConfig {
  code: string;
  name: string;
  path: string;
  icon?: LucideIcon;
  featureKey?: string;
  subModules?: ModuleConfig[];
}

export interface SystemConfig {
  code: string;
  name: string;
  icon: LucideIcon;
  color: string;
  description: string;
  modules: ModuleConfig[];
}

export const SYSTEM_CONFIGS: Record<string, SystemConfig> = {
  fleetone: {
    code: 'fleetone',
    name: 'FleetONE',
    icon: Truck,
    color: 'blue',
    description: 'Gestão completa de frotas',
    modules: [
      { code: 'dashboard', name: 'Dashboard', path: '' },
      { code: 'manager', name: 'Aprovações', path: '/manager' },
      { code: 'vehicles', name: 'Veículos', path: '/vehicles' },
      { code: 'drivers', name: 'Motoristas', path: '/drivers' },
      { code: 'trips', name: 'Viagens', path: '/trips' },
      { code: 'trips-analysis', name: 'Análise de Viagens', path: '/trips-analysis' },
      { code: 'maintenance', name: 'Manutenção', path: '/maintenance' },
      { code: 'fuel', name: 'Combustível', path: '/fuel' },
      { code: 'costs', name: 'Custos', path: '/costs' },
      { code: 'expenses', name: 'Despesas', path: '/expenses' },
      { code: 'documents', name: 'Documentos', path: '/documents' },
      { code: 'alerts', name: 'Alertas', path: '/alerts' },
      { code: 'providers', name: 'Fornecedores', path: '/providers' },
      { code: 'assignments', name: 'Atribuições', path: '/assignments' },
      { code: 'tires', name: 'Pneus', path: '/tires' },
      { code: 'reports', name: 'Relatórios', path: '/reports' },
      { code: 'settings', name: 'Configurações', path: '/settings' },
    ]
  },
  fleet: {
    code: 'fleet',
    name: 'FleetONE',
    icon: Truck,
    color: 'blue',
    description: 'Gestão completa de frotas',
    modules: [
      { code: 'dashboard', name: 'Dashboard', path: '' },
      { code: 'manager', name: 'Aprovações', path: '/manager' },
      {
        code: 'monitoring',
        name: 'Monitoramento',
        path: '/monitoring',
        icon: MapPin,
        subModules: [
          {
            code: 'monitoring_live',
            name: 'Mapa ao Vivo',
            path: '/monitoring/live',
            icon: Map
          },
          {
            code: 'monitoring_geofences',
            name: 'Geocercas',
            path: '/monitoring/geofences',
            icon: Shield
          },
          {
            code: 'monitoring_alerts',
            name: 'Alertas GPS',
            path: '/monitoring/alerts',
            icon: AlertTriangle
          },
          {
            code: 'monitoring_commands',
            name: 'Comandos GPS',
            path: '/monitoring/commands',
            icon: Send
          }
        ]
      },
      {
        code: 'my_fleet',
        name: 'Minha Frota',
        path: '/my-fleet',
        icon: Truck,
        subModules: [
          {
            code: 'my_fleet_vehicles',
            name: 'Veículos',
            path: '/vehicles',
            icon: Car
          },
          {
            code: 'my_fleet_drivers',
            name: 'Motoristas',
            path: '/drivers',
            icon: Users
          },
          {
            code: 'my_fleet_providers',
            name: 'Fornecedores',
            path: '/providers',
            icon: Package
          },
          {
            code: 'my_fleet_equipment',
            name: 'Equipamentos',
            path: '/equipment',
            icon: Radio
          },
          {
            code: 'my_fleet_store',
            name: 'Loja',
            path: '/store',
            icon: ShoppingBag
          }
        ]
      },
      { code: 'trips', name: 'Viagens', path: '/trips' },
      { code: 'trips-analysis', name: 'Análise de Viagens', path: '/trips-analysis' },
      { code: 'maintenance', name: 'Manutenção', path: '/maintenance' },
      { code: 'fuel', name: 'Combustível', path: '/fuel' },
      { code: 'costs', name: 'Custos', path: '/costs' },
      { code: 'expenses', name: 'Despesas', path: '/expenses' },
      { code: 'documents', name: 'Documentos', path: '/documents' },
      { code: 'alerts', name: 'Alertas', path: '/alerts', icon: AlertCircle },
      { code: 'notifications', name: 'Notificações', path: '/notifications', icon: Bell },
      { code: 'assignments', name: 'Atribuições', path: '/assignments' },
      { code: 'tires', name: 'Pneus', path: '/tires' },
      { code: 'reports', name: 'Relatórios', path: '/reports' },
      { 
        code: 'settings', 
        name: 'Configurações', 
        path: '/settings',
        icon: Settings,
        subModules: [
          {
            code: 'settings_gps',
            name: 'GPS / Rastreamento',
            path: '/settings/gps',
            icon: Radio
          }
        ]
      },
    ]
  },
};

export function getSystemConfig(systemCode: string): SystemConfig | undefined {
  return SYSTEM_CONFIGS[systemCode.toLowerCase()];
}

export function getSystemIcon(systemCode: string): LucideIcon {
  const config = SYSTEM_CONFIGS[systemCode.toLowerCase()];
  return config?.icon || FileText;
}

export function getSystemName(systemCode: string): string {
  const config = SYSTEM_CONFIGS[systemCode.toLowerCase()];
  return config?.name || systemCode;
}

// Module icons mapping
export const MODULE_ICONS: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  manager: Shield,
  vehicles: Car,
  drivers: Users,
  trips: Route,
  'trips-analysis': BarChart3,
  maintenance: Wrench,
  fuel: Fuel,
  costs: Coins,
  expenses: Receipt,
  documents: FileText,
  alerts: AlertCircle,
  settings: Settings,
  reports: BarChart3,
  providers: Package,
  assignments: ClipboardList,
  gps: MapPin,
  live: Map,
  geofences: Shield,
  devices: Radio,
  commands: Send,
  notifications: Bell,
  users: Users,
  billing: CreditCard,
};

export function getModuleIcon(moduleCode: string): LucideIcon {
  return MODULE_ICONS[moduleCode] || Package;
}

// Report icons mapping
const REPORT_ICONS: Record<string, LucideIcon> = {
  vehicles: Car,
  drivers: Users,
  maintenance: Wrench,
  fuel: Fuel,
  costs: Coins,
  documents: FileText,
  financial: CreditCard,
  default: BarChart3,
};

export function getReportIcon(reportCode: string): LucideIcon {
  return REPORT_ICONS[reportCode] || REPORT_ICONS.default;
}
